export type JobStatus = 'In Progress' | 'Completed' | 'Archived' | 'Pending' | string;

export interface Job {
  id: number;
  jobNumber: string | null;
  address: string;
  appointmentDate: string | Date | null;
  appointmentTime: string | null;
  status: JobStatus | null;
  photoUrl: string | null;
  reportType?: string | null;
  mcProId?: string | number | null;
}

export interface JobPayload {
  jobNumber?: string | null;
  address: string;
  appointmentDate?: string | null;
  appointmentTime?: string | null;
  status?: JobStatus;
  photoUrl?: string | null;
  reportType?: string | null;
  mcProId?: string | number | null;
}

export interface PropertyPhoto {
  id: number;
  jobId: number;
  photoUrl: string;
  label: string | null;
  description?: string | null;
  imageType?: string | null;
  mcProId?: string | number | null;
  documentUid?: string | null;
  md5Hash?: string | null;
  s3Bucket?: string | null;
  s3Key?: string | null;
  contentType?: string | null;
  fileExtension?: string | null;
  displayOrder?: string | null;
}

export interface ImageListItem {
  value: string;
  label: string;
}

export interface FloorArea {
  id?: string | number | null;
  floorAreasId?: number | null;
  floorAreasNo?: number | null;
  title: string;
  grossArea: number | null;
}

export interface Building {
  id: string;
  multiDwellingImprovementId: number | null;
  isDelete: boolean;
  name: string;
  levels: string;
  style: string;
  streetAppeal: string;
  foundations: string;
  flooring: string;
  mainWalls: string;
  windowFrames: string;
  roof: string;
  interiorLinings: string;
  ceilingLinings: string;
  frame: string;
  bedrooms: number | null;
  bathrooms: number | null;
  livingAreas: number | null;
  accommodationPlus: string;
  pcItems: string;
  fixturesAndFeatures: string;
  numberOfLivingAreas: string;
  carAccommodation: string;
  accommodationDescription: string;
  additionalFeatures: string;
  features: string;
  chattels: string;
  implementsOverview: string;
  briefDescription: string;
  livingAreasSqm: number | null;
  outdoorAreasSqm: number | null;
  otherAreasSqm: number | null;
  carAreasSqm: number | null;
  floorAreas: FloorArea[];
  carSpaces: number | null;
  internalLayout: string;
  internalCondition: string;
  externalCondition: string;
  ancillaryCondition: string;
  mainCarAccomType: string;
  yearOfConstruction: string;
  yearBuilt: string;
  yearOfAdditions: string;
  outdoorArea: string;
  undercoverCarSpaces: number | null;
  significantImprovements: string;
  secondaryImprovements: string;
  essentialRepairs: string;
  water: string;
  drainage: string;
  irrigation: string;
  fencing: string;
  races: string;
  isDuplicate: boolean ;
}

export interface OtherBuilding {
  id: string;
  title: string;
  area: string;
  foundations: string;
  flooring: string;
  walls: string;
  roof: string;
  pcItems: string;
  notes: string;
}

export interface RuralInfo {
  water?: string | null;
  drainage?: string | null;
  irrigation?: string | null;
  fencing?: string | null;
  races?: string | null;
  essentialRepairRequired?: string | null;
  essentialRepairComments?: string | null;
}

export interface ValuationTypeOption {
  valuationTypeId: string;
  valuationType: string;
}

export interface ImprovementsPayload {
  multiDwelling: Building[];
  rural: RuralInfo;
  otherBuildings: OtherBuilding[];
}

export interface PropertyInfo {
  id?: number;
  jobId: number;
  valuationType?: string | null;
  valuationTypeId?: string | null;
  valuationTypeDescription?: string | null;
  valuationTypes?: ValuationTypeOption[] | null;
  propertyType?: string | null;
  siteDimensions?: string | null;
  neighbourhood?: string | null;
  siteDescription?: string | null;
  services?: string | null;
  style?: string | null;
  levels?: string | null;
  streetAppeal?: string | null;
  foundations?: string | null;
  flooring?: string | null;
  mainWalls?: string | null;
  windowFrames?: string | null;
  roof?: string | null;
  interiorLinings?: string | null;
  ceilingLinings?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  livingAreas?: number | null;
  accommodationPlus?: string | null;
  pcItems?: string | null;
  fixturesAndFeatures?: string | null;
  livingAreasSqm?: number | null;
  outdoorAreasSqm?: number | null;
  otherAreasSqm?: number | null;
  carAreasSqm?: number | null;
  internalLayout?: string | null;
  internalCondition?: string | null;
  externalCondition?: string | null;
  ancillaryCondition?: string | null;
  mainCarAccomType?: string | null;
  yearOfConstruction?: string | null;
  yearBuilt?: string | null;
  yearOfAdditions?: string | null;
  outdoorArea?: string | null;
  undercoverCarSpaces?: number | null;
  significantImprovements?: string | null;
  secondaryImprovements?: string | null;
  essentialRepairs?: string | null;
  buildings?: string | null;
  otherBuildings?: string | null;
  notes?: string | null;
  drawingData?: string | null;
  overallPresentation?: string | null;
  carSpaces?: number | null;
  clientNameContactDetails?: string | null;
  clientCompanyName?: string | null;
  clientReportPreparedFor?: string | null;
  clientContactName?: string | null;
  clientPhone?: string | null;
  clientEmail?: string | null;
  squareFootage?: number | null;
  inspectionChecklist?: string | null;
  rural?: RuralInfo | null;
}

export interface AppInfo {
  version: string;
  dataDir: string;
  logsDir: string;
  storageBackend: 'sqlite' | 'json' | 'indexeddb';
  isDesktop: boolean;
  platform: string;
}

export interface ReleaseNotes {
  version: string;
  changes: string[];
}

export interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
}

export const emptyBuilding = (index: number): Building => ({
  id: `building-${Date.now()}-${index}`,
  isDelete:false,
  multiDwellingImprovementId: null,
  name: `Building ${index}`,
  levels: '',
  style: '',
  streetAppeal: '',
  foundations: '',
  flooring: '',
  mainWalls: '',
  windowFrames: '',
  roof: '',
  interiorLinings: '',
  ceilingLinings: '',
  frame: '',
  bedrooms: null,
  bathrooms: null,
  livingAreas: null,
  accommodationPlus: '',
  pcItems: '',
  fixturesAndFeatures: '',
  numberOfLivingAreas: '',
  carAccommodation: '',
  accommodationDescription: '',
  additionalFeatures: '',
  features: '',
  chattels: '',
  implementsOverview: '',
  briefDescription: '',
  livingAreasSqm: null,
  outdoorAreasSqm: null,
  otherAreasSqm: null,
  carAreasSqm: null,
  floorAreas: [],
  carSpaces: null,
  internalLayout: '',
  internalCondition: '',
  externalCondition: '',
  ancillaryCondition: '',
  mainCarAccomType: '',
  yearOfConstruction: '',
  yearBuilt: '',
  yearOfAdditions: '',
  outdoorArea: '',
  undercoverCarSpaces: null,
  significantImprovements: '',
  secondaryImprovements: '',
  essentialRepairs: '',
  water: '',
  drainage: '',
  irrigation: '',
  fencing: '',
  races: '',
  isDuplicate: false,
});

export const emptyOtherBuilding = (index: number): OtherBuilding => ({
  id: `other-building-${Date.now()}-${index}`,
  title: '',
  area: '0.00',
  foundations: '',
  flooring: '',
  walls: '',
  roof: '',
  pcItems: '',
  notes: '',
});
